@file:OptIn(androidx.compose.ui.ExperimentalComposeUiApi::class)

package com.trevorwiebe.timesheet.invoicing.presentation

import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.Card
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.backhandler.BackHandler
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDatePicker
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveWidth
import com.trevorwiebe.timesheet.core.presentation.common.TimeSheetButton
import com.trevorwiebe.timesheet.core.presentation.common.TimesheetTextField
import com.trevorwiebe.timesheet.core.presentation.common.responsiveDialogWidth
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice

@Composable
internal fun ReceivePaymentContent(state: InvoicingState, onEvent: (InvoicingEvent) -> Unit) {
    val form = state.receivePayment ?: return
    BackHandler { onEvent(InvoicingEvent.OnDismissReceivePayment) }
    val invoices = state.paymentOpenInvoices
    val decimalKeyboard = KeyboardOptions(keyboardType = KeyboardType.Decimal)

    Scaffold(
        topBar = {
            TopBar(
                title = "Receive Payment",
                backIcon = { BackIcon() },
                onBack = { onEvent(InvoicingEvent.OnDismissReceivePayment) },
            )
        },
        bottomBar = {
            ResponsiveWidth {
                Column(Modifier.fillMaxWidth().navigationBarsPadding().imePadding().padding(12.dp)) {
                    val message = form.error ?: state.paymentProblem.takeIf { form.amountText.isNotBlank() }
                    message?.let {
                        Text(
                            text = it,
                            color = MaterialTheme.colorScheme.error,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(bottom = 6.dp),
                        )
                    }
                    TimeSheetButton(
                        onClick = { onEvent(InvoicingEvent.OnSavePayment) },
                        text = "Save payment",
                        modifier = Modifier.fillMaxWidth(),
                        loading = form.isSaving,
                        enabled = state.canSavePayment,
                    )
                }
            }
        },
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
            LazyColumn(Modifier.fillMaxSize()) {
                item {
                    Column(Modifier.padding(horizontal = 12.dp, vertical = 8.dp)) {
                        PaymentCustomerPicker(state = state, selectedId = form.customerId, onEvent = onEvent)
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            TimesheetTextField(
                                modifier = Modifier.weight(1f),
                                text = form.amountText,
                                label = "Payment amount ($)",
                                singleLine = true,
                                keyboardOptions = decimalKeyboard,
                                selectAllOnFocus = true,
                                onTextChange = { onEvent(InvoicingEvent.OnPaymentAmountChanged(it)) },
                            )
                            // Read-only field with a clickable overlay that opens the date picker.
                            Box(Modifier.weight(1f)) {
                                TimesheetTextField(
                                    modifier = Modifier.fillMaxWidth(),
                                    text = Util.toFriendlyDate(form.date),
                                    label = "Received",
                                    singleLine = true,
                                    readOnly = true,
                                )
                                Box(
                                    Modifier
                                        .matchParentSize()
                                        .clickable { onEvent(InvoicingEvent.OnPaymentDateClick) }
                                )
                            }
                        }
                    }
                    HorizontalDivider()
                }
                when {
                    form.customerId.isEmpty() -> item {
                        Message("Choose who the payment is from to see their open invoices.")
                    }
                    invoices.isEmpty() -> item {
                        Message("This customer has no sent invoices with a balance.")
                    }
                    else -> {
                        item {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 10.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    text = "Open invoices, oldest first",
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.SemiBold,
                                    modifier = Modifier.weight(1f),
                                )
                                Text(
                                    text = "Unapplied: ${state.paymentUnappliedFormatted}",
                                    fontSize = 14.sp,
                                    color = if (state.paymentUnapplied == 0) MaterialTheme.colorScheme.onSurface
                                    else MaterialTheme.colorScheme.error,
                                )
                            }
                            HorizontalDivider()
                        }
                        items(invoices, key = { it.id }) { invoice ->
                            OpenInvoiceRow(
                                invoice = invoice,
                                amountText = form.allocationTexts[invoice.id].orEmpty(),
                                onAmountChange = { onEvent(InvoicingEvent.OnAllocationChanged(invoice.id, it)) },
                            )
                        }
                    }
                }
            }
        }
    }

    NativeDatePicker(
        date = form.date,
        showDatePicker = form.showDatePicker,
        onDismiss = { onEvent(InvoicingEvent.OnDismissPaymentDatePicker) },
        onDateSelected = { onEvent(InvoicingEvent.OnPaymentDateSelected(it)) },
    )
}

@Composable
private fun Message(text: String) {
    Text(
        text = text,
        textAlign = TextAlign.Center,
        color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        modifier = Modifier.fillMaxWidth().padding(horizontal = 24.dp, vertical = 32.dp),
    )
}

@Composable
private fun OpenInvoiceRow(invoice: Invoice, amountText: String, onAmountChange: (String) -> Unit) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(Modifier.weight(1f)) {
            Text(
                text = "${invoice.numberLabel}  •  ${Util.toFriendlyDate(invoice.invoiceDate)}",
                fontSize = 14.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Text(
                text = "${invoice.balanceFormatted} due of ${invoice.totalFormatted}",
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
            )
        }
        Spacer(Modifier.width(12.dp))
        TimesheetTextField(
            modifier = Modifier.width(140.dp),
            text = amountText,
            label = "Apply ($)",
            singleLine = true,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            selectAllOnFocus = true,
            onTextChange = onAmountChange,
        )
    }
    HorizontalDivider()
}

@Composable
private fun PaymentCustomerPicker(state: InvoicingState, selectedId: String, onEvent: (InvoicingEvent) -> Unit) {
    var showDialog by remember { mutableStateOf(false) }
    val selected = state.customers.firstOrNull { it.id == selectedId }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(bottom = 8.dp)
            .clip(RoundedCornerShape(4.dp))
            .border(1.dp, MaterialTheme.colorScheme.primary, RoundedCornerShape(4.dp))
            .clickable { showDialog = true }
            .padding(horizontal = 16.dp, vertical = 14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = selected?.let { state.customerName(it) } ?: "Who is the payment from?",
            color = if (selected == null) MaterialTheme.colorScheme.onSurfaceVariant
            else MaterialTheme.colorScheme.onBackground,
            modifier = Modifier.weight(1f),
        )
        Icon(Icons.AutoMirrored.Filled.KeyboardArrowRight, contentDescription = null)
    }
    if (showDialog) {
        val summaries = state.openSummaryByCustomer
        // Only customers who owe something can be paid; listing everyone would just be noise.
        val owing = state.sortedCustomers.filter { it.id in summaries }
        Dialog(onDismissRequest = { showDialog = false }) {
            Card(modifier = Modifier.responsiveDialogWidth(), shape = RoundedCornerShape(16.dp)) {
                Column(Modifier.padding(top = 20.dp, bottom = 8.dp)) {
                    Text(
                        text = "Who is the payment from?",
                        fontSize = 20.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.padding(horizontal = 24.dp),
                    )
                    Spacer(Modifier.height(12.dp))
                    HorizontalDivider()
                    if (owing.isEmpty()) {
                        Message("No customers have sent invoices waiting on payment.")
                    }
                    LazyColumn(Modifier.heightIn(max = 440.dp)) {
                        items(owing, key = { it.id }) { customer ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        showDialog = false
                                        onEvent(InvoicingEvent.OnPaymentCustomerSelected(customer.id))
                                    }
                                    .padding(horizontal = 24.dp, vertical = 14.dp),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Text(
                                    text = state.customerName(customer),
                                    fontSize = 16.sp,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier.weight(1f),
                                )
                                Text(
                                    text = summaries[customer.id].orEmpty(),
                                    fontSize = 13.sp,
                                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
                                    modifier = Modifier.padding(start = 12.dp),
                                )
                            }
                        }
                    }
                    HorizontalDivider()
                    Box(Modifier.fillMaxWidth().padding(horizontal = 12.dp), contentAlignment = Alignment.CenterEnd) {
                        TextButton(onClick = { showDialog = false }) {
                            Text("Cancel", color = MaterialTheme.colorScheme.tertiary)
                        }
                    }
                }
            }
        }
    }
}
